import { Button, Icon, Text } from '@flatfile/baseline'
import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AutobuildWelcomeModal } from '../../../components/AutobuildWelcomeModal'
import { mixpanel } from '../../../support/mixpanel'

export const ACCEPTED_XLSX_FILE_EXTENSIONS = [
  '.xls', // application/vnd.ms-excel
  '.xlsx', // application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
  '.xlsm', // application/vnd.ms-excel.sheet.macroEnabled.12]
]

// File extensions corresponding to the list on orchestrator
const ACCEPTED_FILE_EXTENSIONS = [
  '.csv', // text/csv
  '.txt', // text/plain
  '.text', // text/plain
  '.docx', // application/vnd.openxmlformats-officedocument.wordprocessingml.document
  '.doc', // application/msword
  '.pdf', // application/pdf
  '.json', // application/json
  ...ACCEPTED_XLSX_FILE_EXTENSIONS,
]

export type AutoBuildParams = {
  files?: File[]
  prompt?: string
  empty?: boolean
}

type ConfigureAgentProps = {
  onSubmit: (params: AutoBuildParams) => void
  error?: string
}

// File with upload status
type FileWithStatus = {
  file: File
  status: 'complete' | 'error'
  errorMessage?: string
}

const MAX_FILE_COUNT = 20
const MAX_TOTAL_SIZE = 2_147_483_648 // 2GB in bytes

function isFileValid(file: File): { valid: boolean } {
  // Check by file extension
  const fileName = file.name.toLowerCase()
  const fileExtensionValid = ACCEPTED_FILE_EXTENSIONS.some((ext) => fileName.endsWith(ext))
  if (fileExtensionValid) {
    return { valid: true }
  }
  return { valid: false }
}

function getValidatedFiles(
  currentFiles: FileWithStatus[],
  incomingFiles: File[]
): { validFiles: FileWithStatus[]; error: string | null; hasInvalidFiles: boolean } {
  const newFiles: FileWithStatus[] = []
  let hasInvalidFiles = false
  incomingFiles.forEach((file) => {
    const { valid } = isFileValid(file)
    if (valid) {
      newFiles.push({ file, status: 'complete' })
    } else {
      hasInvalidFiles = true
    }
  })
  if (currentFiles.length + newFiles.length > MAX_FILE_COUNT) {
    return {
      validFiles: [],
      error: 'You can upload a maximum of 20 files. Please remove some files before adding more.',
      hasInvalidFiles,
    }
  }
  const currentTotalSize = currentFiles.reduce((sum, item) => sum + item.file.size, 0)
  const newFilesTotalSize = newFiles.reduce((sum, item) => sum + item.file.size, 0)
  if (currentTotalSize + newFilesTotalSize > MAX_TOTAL_SIZE) {
    return {
      validFiles: [],
      error: 'You can upload a maximum total file size of 2GB. Please remove some files before adding more.',
      hasInvalidFiles,
    }
  }
  if (hasInvalidFiles) {
    return {
      validFiles: newFiles,
      error: 'One or more files are of unsupported type and were not added.',
      hasInvalidFiles,
    }
  }
  return { validFiles: newFiles, error: null, hasInvalidFiles: false }
}

export const ConfigureAgentForm: React.FC<ConfigureAgentProps> = ({ onSubmit, error }) => {
  const [fileList, setFileList] = useState<FileWithStatus[]>([])
  const [prompt, setPrompt] = useState('')
  const [isPromptExpanded, setIsPromptExpanded] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)
  const [searchParams] = useSearchParams()

  // Detect if we're in onboarding mode
  const isOnboardingMode = searchParams.get('welcome') === 'true'

  const handleSubmit = useCallback(
    (fromScratch?: boolean) => {
      if (fromScratch) {
        // Track "Create from scratch" submissions
        mixpanel.track(isOnboardingMode ? 'Buildmode Onboarding Form Submitted' : 'Autobuild Form Submitted', {
          submissionType: 'empty',
          isOnboarding: isOnboardingMode,
          hasFiles: false,
          hasPrompt: false,
          fileCount: 0,
          promptLength: 0,
        })
        onSubmit({ empty: true })
      } else {
        const params: AutoBuildParams = {}
        let submissionType = 'unknown'
        const hasFiles = fileList.length > 0
        const hasPrompt = prompt.trim().length > 0
        const validFiles = fileList.filter((item) => item.status === 'complete')
        const fileCount = validFiles.length

        // Include files if they exist
        if (hasFiles) {
          if (validFiles.length > 0) {
            params.files = validFiles.map((item) => item.file)
          }
        }

        // Include prompt if it exists
        if (hasPrompt) {
          params.prompt = prompt.trim()
        }

        // Determine submission type
        if (hasFiles && hasPrompt) {
          submissionType = 'files_and_prompt'
        } else if (hasFiles) {
          submissionType = 'files_only'
        } else if (hasPrompt) {
          submissionType = 'prompt_only'
        }

        // Only submit if we have at least one valid parameter
        if (params.files?.length || params.prompt) {
          // Track successful form submission
          mixpanel.track(isOnboardingMode ? 'Buildmode Onboarding Form Submitted' : 'Autobuild Form Submitted', {
            submissionType,
            isOnboarding: isOnboardingMode,
            hasFiles,
            hasPrompt,
            fileCount,
            promptLength: prompt.trim().length,
            hasXlsxFiles: validFiles.some((item) => {
              const ext = item.file.name.slice(item.file.name.lastIndexOf('.')).toLowerCase()
              return ACCEPTED_XLSX_FILE_EXTENSIONS.includes(ext)
            }),
          })
          onSubmit(params)
        }
      }
    },
    [fileList, prompt, onSubmit, isOnboardingMode]
  )

  const handleFileDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement> | React.ChangeEvent<HTMLInputElement>) => {
      let droppedFiles: FileList | null = null
      if ('dataTransfer' in event) {
        event.preventDefault()
        droppedFiles = event.dataTransfer.files
      } else if ('target' in event && event.target.files) {
        droppedFiles = event.target.files
      }
      setFileError(null)
      if (droppedFiles && droppedFiles.length > 0) {
        const { validFiles, error, hasInvalidFiles } = getValidatedFiles(fileList, Array.from(droppedFiles))
        if (error) {
          setFileError(error)
          // Track file validation errors
          mixpanel.track(isOnboardingMode ? 'Buildmode Onboarding Form Error' : 'Autobuild Form Error', {
            errorType: 'file_validation',
            isOnboarding: isOnboardingMode,
            errorMessage: error,
            hasInvalidFiles,
            attemptedFileCount: droppedFiles.length,
            currentFileCount: fileList.length,
            validFilesInAttempt: validFiles.length,
          })
        }
        if (validFiles.length > 0) {
          setFileList([...fileList, ...validFiles])
        }
      }
    },
    [fileList, isOnboardingMode]
  )

  const handleRemoveFile = useCallback((fileToRemove: FileWithStatus) => {
    setFileList((prevList) => {
      const updatedList = prevList.filter(
        (item) => !(item.file.name === fileToRemove.file.name && item.file.size === fileToRemove.file.size)
      )

      // Clear file error if no error files remain
      if (!updatedList.some((item) => item.status === 'error')) {
        setFileError(null)
      }

      return updatedList
    })
  }, [])

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }, [])

  const handlePaste = useCallback(
    (event: ClipboardEvent) => {
      const clipboardData = event.clipboardData
      if (clipboardData?.items?.length) {
        const items = Array.from(clipboardData.items)
        const pastedFiles = items
          .filter((item) => item.kind === 'file')
          .map((item) => item.getAsFile())
          .filter(Boolean) as File[]
        if (pastedFiles.length > 0) {
          const { validFiles, error, hasInvalidFiles } = getValidatedFiles(fileList, pastedFiles)
          if (error) {
            setFileError(error)
            // Track paste validation errors
            mixpanel.track(isOnboardingMode ? 'Buildmode Onboarding Form Error' : 'Autobuild Form Error', {
              errorType: 'paste_validation',
              isOnboarding: isOnboardingMode,
              errorMessage: error,
              hasInvalidFiles,
              attemptedFileCount: pastedFiles.length,
              currentFileCount: fileList.length,
              validFilesInAttempt: validFiles.length,
            })
          }
          if (validFiles.length > 0) {
            setFileList([...fileList, ...validFiles])
          }
        } else {
          const text = clipboardData.getData('text')
          if (text) {
            setPrompt(text)
          }
        }
      }
    },
    [fileList, isOnboardingMode]
  )

  const togglePromptSection = useCallback(() => {
    setIsPromptExpanded((prev) => !prev)
  }, [])

  const handlePromptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value)
  }, [])

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      document.getElementById('file-upload')?.click()
    }
  }, [])

  useEffect(() => {
    document.addEventListener('paste', handlePaste)
    return () => {
      document.removeEventListener('paste', handlePaste)
    }
  }, [handlePaste])

  // Track when server errors are shown to users
  useEffect(() => {
    if (error) {
      mixpanel.track(isOnboardingMode ? 'Buildmode Onboarding Form Error' : 'Autobuild Form Error', {
        errorType: 'server_error',
        isOnboarding: isOnboardingMode,
        errorMessage: error,
      })
    }
  }, [error, isOnboardingMode])

  return (
    <div className='relative w-full flex flex-col h-[calc(100vh-64px)]'>
      <AutobuildWelcomeModal />
      {/* Upload section - 75% of the viewport height */}
      <div className='w-full h-full min-h-[480px] flex items-center justify-center overflow-y-auto'>
        <div className='bg-popover w-[660px] max-w-[90%] p-5 rounded-md shadow-sm mx-auto my-8'>
          <div className='flex flex-col w-full items-center'>
            <Text variant='largeTitle' emphasized className='mb-6 text-center font-normal'>
              Build your app with Autobuild
            </Text>

            <div
              className='self-stretch border border-dashed border-border-table-border rounded-md min-h-[180px] flex flex-col items-center justify-center cursor-pointer bg-surface-base-elevated transition-colors w-full'
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
              onClick={() => document.getElementById('file-upload')?.click()}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              role='button'
              aria-label='Upload files'
            >
              <div className='flex flex-col items-center justify-center p-6 gap-2'>
                <Text variant='body' className='text-center inline-flex items-center gap-1'>
                  Drop or <Icon name='folder-open' size={12} className='ml-1' />
                  <span className='text-primary underline'>Upload files</span>
                </Text>
                <Text variant='callout' className='text-center text-text-inverted-muted mt-1'>
                  You can also paste (CMD+V)
                </Text>
                <input
                  id='file-upload'
                  type='file'
                  className='hidden'
                  onChange={handleFileDrop}
                  multiple
                  accept={ACCEPTED_FILE_EXTENSIONS.join(',')}
                />
              </div>
            </div>

            {/* File validation error */}
            {fileError && (
              <Text variant='callout' className='mt-4 text-destructive'>
                {fileError}
              </Text>
            )}

            {/* Display uploaded files */}
            {fileList.length > 0 && (
              <div className='w-full flex flex-wrap gap-3 mt-4 items-center justify-center'>
                {fileList.map((fileItem, index) => (
                  <div
                    key={`${fileItem.file.name}-${index}`}
                    className={`border ${fileItem.status === 'error' ? 'border-destructive/30 bg-destructive/10' : 'border-border-input'} rounded-sm flex items-center justify-between p-2 w-[545px]`}
                  >
                    <div className='flex items-center gap-2 overflow-hidden'>
                      <Icon name='file-bend' size={16} className='flex-shrink-0' />
                      <Text variant='body' className='truncate'>
                        {fileItem.file.name}
                      </Text>
                    </div>
                    <div className='flex-shrink-0 ml-1'>
                      {fileItem.status === 'error' ? (
                        <div className='flex items-center gap-2'>
                          <Text variant='callout' className='text-destructive truncate max-w-[120px]'>
                            {fileItem.errorMessage || 'Error'}
                          </Text>
                          <Button
                            variant='secondary'
                            size='icon-sm'
                            icon='cross-small'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRemoveFile(fileItem)
                            }}
                          ></Button>
                        </div>
                      ) : (
                        <div className='flex gap-2'>
                          <Icon name='circle-check' size={16} className='text-success' />
                          <Button
                            variant='secondary'
                            size='icon-sm'
                            icon='cross-small'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRemoveFile(fileItem)
                            }}
                          ></Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Prompt section - wrapper */}
            <div className='w-full mt-4 flex flex-col items-center relative'>
              {/* Button always stays in the same position */}
              <Button
                variant='ghost'
                onClick={togglePromptSection}
                className='text-primary'
                size='sm'
                icon={isPromptExpanded ? 'chevron-top' : 'chevron-bottom'}
                iconPlacement='right'
                iconProps={{ className: 'text-primary', size: 12 }}
              >
                Add a prompt or paste text
              </Button>

              {/* Textarea container with transition */}
              <div
                className={`w-full transition-opacity duration-200 ease-in-out ${
                  isPromptExpanded
                    ? 'max-h-[200px] opacity-100 mt-2 pointer-events-auto'
                    : 'max-h-0 opacity-0 mt-0 pointer-events-none overflow-hidden'
                }`}
                style={{
                  transitionProperty: 'opacity, max-height, margin',
                  transitionDuration: '200ms, 200ms, 200ms',
                }}
              >
                <textarea
                  className='w-full border border-border-input rounded-md p-2 min-h-[72px] h-[72px] resize-none focus:outline-none focus:ring-primary focus:ring-1 text-sm bg-base-white'
                  placeholder='Start with a prompt, add additional instructions or paste in your data template here...'
                  value={prompt}
                  onChange={handlePromptChange}
                  disabled={!isPromptExpanded}
                />
              </div>
            </div>

            {/* Action button */}
            <div className='mt-6 flex justify-center w-full'>
              <Button
                variant='primary'
                onClick={() => handleSubmit()}
                disabled={(!fileList.length || fileList.every((item) => item.status === 'error')) && !prompt.trim()}
                icon='arrow-right'
                iconPlacement='right'
                iconProps={{ size: 12 }}
              >
                Get Started
              </Button>
            </div>

            {error && (
              <Text variant='body' className='mt-4 text-destructive text-center'>
                {error}
              </Text>
            )}
          </div>
        </div>
      </div>

      {/* Bottom section - 25% of the viewport height */}
      <div className='w-full h-[150px] flex items-end justify-center pb-8'>
        <div className='flex flex-col items-center max-w-[90%] px-4'>
          <Text variant='subheadline' emphasized className='text-[#4B143DDD] mb-1'>
            Developer
          </Text>
          <Text variant='headline' emphasized>
            Start from scratch
          </Text>
          <Text variant='body' className='text-center my-3 max-w-[450px]'>
            Use the power of the Flatfile Data Exchange Platform to fit your use case.
          </Text>
          <div className='flex gap-3'>
            <Button
              variant='ghost'
              onClick={() => window.open('https://flatfile.com/docs', '_blank', 'noopener,noreferrer')}
              className='text-[#3B2FC9]'
            >
              Developer Docs
            </Button>
            <Button
              variant='outline'
              onClick={() => handleSubmit(true)}
              className='flex items-center gap-1'
              icon='arrow-right'
              iconPlacement='right'
              iconProps={{ size: 12 }}
            >
              Create app from scratch
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
